import { useEffect } from 'react';
import { observer } from 'mobx-react';
import useUtahHeader from './useUtahHeader';
import UtahHeaderConfig from './UtahHeaderConfig';
import appStore from '../mobx/appStore';
import menuStore from '../mobx/menuStore';

// The Utah Design System "selected" highlight modifier for a menu item link.
const SELECTED_CLASS = 'menu-item--selected';

// UDS renders each main-menu link with an id of the form
// `menu-item__link-title__<TitleWithoutSpaces>-<uid>` (see the header UMD's
// renderMainMenu), and the same item can appear in both the horizontal menu and
// the mobile side panel. So a menu item's links are matched by this id prefix.
const idPrefixForTitle = (title) =>
  `menu-item__link-title__${String(title || '').replaceAll(' ', '')}-`;

// Mark the clicked menu item as selected: add `menu-item--selected` to the
// link(s) for `title` and remove it from every other main-menu link (i.e. the
// previously selected one). Exported for unit testing.
export const selectMenuItem = (title) => {
  if (typeof document === 'undefined' || !document.querySelectorAll) {
    return;
  }
  const prefix = idPrefixForTitle(title);
  document
    .querySelectorAll('[id^="menu-item__link-title__"]')
    .forEach((el) => {
      if (el.id.startsWith(prefix)) {
        el.classList.add(SELECTED_CLASS);
      } else {
        el.classList.remove(SELECTED_CLASS);
      }
    });
};

// Map the app's menuStore items to Utah Design System mainMenu menuItems. The
// menu onClick handlers are SPA navigations, so they are wired as actionFunction
// (which UDS assigns as the link's onclick). Each click also marks the clicked
// item selected and clears the previously selected one.
export const toMenuItems = (items) =>
  (items || []).map((item) => ({
    title: item.text,
    actionFunction: (event) => {
      selectMenuItem(item.text);
      if (typeof item.onClick === 'function') {
        return item.onClick(event);
      }
      return undefined;
    },
  }));

// The Utah Design System header hard-codes a "Sign Out" (and "UtahID Profile")
// entry into the Utah ID wrapper button's popup menu. That Sign Out is KEPT: the
// header config wires `utahId.onSignOut` to the same `signOutEverywhere` full
// logout as the main-menu "Sign Out", so both controls end the Firebase session
// AND the id.utah.gov SSO session identically.

// Translate the app's logged-in user into the header's currentUser shape. null
// tells the header the app controls authentication and there is no current user
// (so it shows the Sign In button).
const toCurrentUser = (loggedInUser) => {
  if (!loggedInUser) {
    return null;
  }
  const [first, ...rest] = (loggedInUser.name || '').split(' ');
  return { authenticated: true, first, last: rest.join(' ') };
};

const UtahHeader = () => {
  const utahHeader = useUtahHeader();

  // Read the observables during render so the observer re-runs the effect when
  // the auth state or menu changes.
  const { loggedInUser, currentOrg } = appStore;
  const { mainMenu } = menuStore;

  useEffect(() => {
    if (!utahHeader) {
      return;
    }

    const base = UtahHeaderConfig();
    utahHeader.setUtahHeaderSettings({
      ...base,
      mainMenu: {
        title: 'UID Pharmacy Main Menu',
        menuItems: toMenuItems(mainMenu),
      },
      utahId: {
        ...base.utahId,
        currentUser: toCurrentUser(loggedInUser),
      },
    });

    // The Utah ID wrapper popup's built-in "Sign Out" is intentionally KEPT: it
    // invokes utahId.onSignOut (UtahHeaderConfig) → signOutEverywhere, the same
    // full logout as the main-menu "Sign Out".
  }, [utahHeader, loggedInUser, currentOrg, mainMenu]);

  return null;
};

export default observer(UtahHeader);
